/**
 * Agent form component with Vuexy styling.
 * Provides form for creating and editing agents with organized sections.
 */

// React Imports
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// MUI Imports
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

// Component Imports
import { TextField } from '@core/components/mui';
import { agentService } from '../../services/agentService';
import { llmService } from '../../services/llmService';
import { useNotificationStore } from '../../store/notificationStore';
import { ParameterList } from './ParameterList';
import { CodeEditor } from '../common/CodeEditor';

// Types
import type {
  AgentDefinitionDto,
  CreateAgentRequest,
  UpdateAgentRequest,
  CreateAgentParameterRequest,
  UpdateAgentParameterRequest,
} from '../../types/agent';
import type { LlmModelDto } from '../../types/llm';

const CATEGORIES = [
  'Infrastructure Management',
  'Security & Compliance',
  'Financial Operations',
  'User Support',
  'Monitoring & Observability',
  'Automation & Orchestration',
];

/**
 * Agent form component for creating and editing agents.
 */
export const AgentForm = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [_agent, setAgent] = useState<AgentDefinitionDto | null>(null);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form fields
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('');
  const [userPromptTemplate, _setUserPromptTemplate] = useState('');
  const [category, setCategory] = useState('');
  const [iconName, setIconName] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [includeAllDefaultTools, setIncludeAllDefaultTools] = useState(false);
  const [fanOut, setFanOut] = useState(false);
  const [useOpenAiHostedSkills, setUseOpenAiHostedSkills] = useState(false);
  const [injectSkillInstructionsAsUserContext, setInjectSkillInstructionsAsUserContext] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [defaultModelId, setDefaultModelId] = useState<string>('');
  const [contextLimitLastNMessages, setContextLimitLastNMessages] = useState<string>('');
  const [contextLimitLastNDays, setContextLimitLastNDays] = useState<string>('');
  const [availableModels, setAvailableModels] = useState<LlmModelDto[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [parameters, setParameters] = useState<AgentDefinitionDto['parameters']>([]);

  const { addNotification } = useNotificationStore();

  const selectedModelSupportsHostedSkills = useMemo(() => {
    if (!defaultModelId) return false;
    return (
      availableModels.find((m) => m.id === defaultModelId)?.providerSupportsHostedSkills ?? false
    );
  }, [defaultModelId, availableModels]);

  useEffect(() => {
    if (!defaultModelId || !useOpenAiHostedSkills) return;
    const m = availableModels.find((x) => x.id === defaultModelId);
    if (m && !m.providerSupportsHostedSkills) {
      setUseOpenAiHostedSkills(false);
    }
  }, [defaultModelId, availableModels, useOpenAiHostedSkills]);

  useEffect(() => {
    if (isEdit && id) {
      loadAgent();
    }
    loadAvailableModels();
  }, [id, isEdit]);

  const loadAvailableModels = async () => {
    setModelsLoading(true);
    try {
      const models = await llmService.getAvailableModels();
      setAvailableModels(models);
    } catch {
      // Silently fail - models dropdown will just be empty
      // Could also try loading all models as fallback
      try {
        const allModels = await llmService.getModels(undefined, true);
        setAvailableModels(allModels);
      } catch {
        // ignore
      }
    } finally {
      setModelsLoading(false);
    }
  };

  const loadAgent = async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const data = await agentService.getAgent(id);
      setAgent(data);
      setName(data.name);
      setSlug(data.slug);
      setDescription(data.description || '');
      setSystemPrompt(data.systemPrompt);
      setCategory(data.category || '');
      setIconName(data.iconName || '');
      setSortOrder(data.sortOrder);
      setIncludeAllDefaultTools(data.includeAllDefaultTools);
      setFanOut(data.fanOut ?? false);
      const hostedOk =
        !data.defaultModelId ||
        (data.defaultModelProviderSupportsHostedSkills ?? false);
      setUseOpenAiHostedSkills(hostedOk && (data.useOpenAiHostedSkills ?? false));
      setInjectSkillInstructionsAsUserContext(data.injectSkillInstructionsAsUserContext ?? false);
      setIsActive(data.isActive);
      setDefaultModelId(data.defaultModelId || '');
      setContextLimitLastNMessages(data.contextLimitLastNMessages != null ? String(data.contextLimitLastNMessages) : '');
      setContextLimitLastNDays(data.contextLimitLastNDays != null ? String(data.contextLimitLastNDays) : '');
      setParameters(data.parameters);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load agent');
      addNotification(err.response?.data?.message || 'Failed to load agent', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate slug from name
  useEffect(() => {
    if (!isEdit && name) {
      const generatedSlug = name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setSlug(generatedSlug);
    }
  }, [name, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!systemPrompt.trim()) {
      setError('System prompt is required');
      return;
    }

    const nMessages = contextLimitLastNMessages.trim() ? parseInt(contextLimitLastNMessages.trim(), 10) : null;
    const nDays = contextLimitLastNDays.trim() ? parseInt(contextLimitLastNDays.trim(), 10) : null;
    if (nMessages != null && (Number.isNaN(nMessages) || nMessages < 1 || nMessages > 500)) {
      setError('Last N messages must be between 1 and 500, or leave empty for no limit.');
      return;
    }
    if (nDays != null && (Number.isNaN(nDays) || nDays < 1 || nDays > 365)) {
      setError('Last N days must be between 1 and 365, or leave empty for no limit.');
      return;
    }

    setSaving(true);
    try {
      const originalParamIds = new Set(_agent?.parameters.map(p => p.id) ?? []);

      const params = parameters.map((p) => ({
        ...(isEdit && originalParamIds.has(p.id) ? { id: p.id } : {}),
        key: p.key,
        label: p.label,
        description: p.description,
        type: p.type,
        defaultValue: p.defaultValue,
        isRequired: p.isRequired,
        isSecret: p.isSecret,
        validationJson: p.validationJson,
      }));

      if (isEdit && id) {
        const updateData: UpdateAgentRequest = {
          name: name.trim(),
          slug: slug.trim(),
          description: description.trim() || undefined,
          systemPrompt: systemPrompt.trim(),
          userPromptTemplate: userPromptTemplate.trim() || undefined,
          category: category || undefined,
          iconName: iconName.trim() || undefined,
          sortOrder,
          includeAllDefaultTools,
          fanOut,
          useOpenAiHostedSkills: selectedModelSupportsHostedSkills && useOpenAiHostedSkills,
          injectSkillInstructionsAsUserContext,
          isActive,
          contextLimitLastNMessages: nMessages ?? null,
          contextLimitLastNDays: nDays ?? null,
          defaultModelId: defaultModelId || undefined,
          parameters: params as UpdateAgentParameterRequest[],
        };
        await agentService.updateAgent(id, updateData);
        addNotification('Agent updated successfully', 'success');
      } else {
        const createData: CreateAgentRequest = {
          name: name.trim(),
          slug: slug.trim() || undefined,
          description: description.trim() || undefined,
          systemPrompt: systemPrompt.trim(),
          userPromptTemplate: userPromptTemplate.trim() || undefined,
          category: category || undefined,
          iconName: iconName.trim() || undefined,
          sortOrder,
          includeAllDefaultTools,
          fanOut,
          useOpenAiHostedSkills: selectedModelSupportsHostedSkills && useOpenAiHostedSkills,
          injectSkillInstructionsAsUserContext,
          contextLimitLastNMessages: nMessages ?? undefined,
          contextLimitLastNDays: nDays ?? undefined,
          defaultModelId: defaultModelId || undefined,
          parameters: params as CreateAgentParameterRequest[],
        };
        const created = await agentService.createAgent(createData);
        addNotification('Agent created successfully', 'success');
        navigate(`/agents/${created.id}`);
        return;
      }

      navigate(`/agents/${id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save agent');
      addNotification(err.response?.data?.message || 'Failed to save agent', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleParameterAdd = (param: CreateAgentParameterRequest) => {
    const newParam: AgentDefinitionDto['parameters'][0] = {
      id: crypto.randomUUID(),
      key: param.key,
      label: param.label,
      description: param.description,
      type: param.type,
      defaultValue: param.defaultValue,
      isRequired: param.isRequired,
      isSecret: param.isSecret,
      validationJson: param.validationJson,
    };
    setParameters([...parameters, newParam]);
  };

  const handleParameterUpdate = (paramId: string, param: UpdateAgentParameterRequest) => {
    setParameters(
      parameters.map((p) => (p.id === paramId ? { ...p, ...param } : p))
    );
  };

  const handleParameterDelete = (paramId: string) => {
    setParameters(parameters.filter((p) => p.id !== paramId));
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent sx={{ p: 6 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <IconButton onClick={() => navigate('/agents')} sx={{ mr: -1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" sx={{ fontWeight: 600, flex: 1 }}>
              {isEdit ? 'Edit Agent' : 'Create Agent'}
            </Typography>
          </Box>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Basic Information */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Basic Information
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <TextField
                  label="Name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  fullWidth
                />
                <TextField
                  label="Slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  required
                  fullWidth
                  disabled={isEdit}
                  helperText="URL-friendly identifier (cannot be changed after creation)"
                />
                <TextField
                  label="Description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={category}
                      label="Category"
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {CATEGORIES.map((cat) => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Icon Name"
                    value={iconName}
                    onChange={(e) => setIconName(e.target.value)}
                    fullWidth
                    helperText="Icon identifier (e.g., 'server', 'shield')"
                  />
                  <TextField
                    label="Sort Order"
                    type="number"
                    value={sortOrder}
                    onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
                    fullWidth
                    sx={{ maxWidth: 150 }}
                  />
                </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeAllDefaultTools}
                          onChange={(e) => setIncludeAllDefaultTools(e.target.checked)}
                        />
                      }
                      label="Include All Default Tools"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={fanOut}
                          onChange={(e) => setFanOut(e.target.checked)}
                        />
                      }
                      label="FanOut PDF (run prompt per page)"
                    />
                    {selectedModelSupportsHostedSkills && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={useOpenAiHostedSkills}
                            onChange={(e) => setUseOpenAiHostedSkills(e.target.checked)}
                          />
                        }
                        label="OpenAI hosted skills"
                      />
                    )}
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={injectSkillInstructionsAsUserContext}
                          onChange={(e) => setInjectSkillInstructionsAsUserContext(e.target.checked)}
                        />
                      }
                      label="Inject skill instructions as user context (aligns with OpenAI hosted-skills prompt behavior)"
                    />
                    {isEdit && (
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                          />
                        }
                        label="Active"
                      />
                    )}
                  </Box>
                </Box>
              </Box>

              <Divider />

              {/* Model Selection */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Default Model
                </Typography>
                <FormControl fullWidth>
                  <InputLabel>Select Model</InputLabel>
                  <Select
                    value={defaultModelId}
                    label="Select Model"
                    onChange={(e) => setDefaultModelId(e.target.value)}
                    disabled={modelsLoading}
                  >
                    <MenuItem value="">
                      <em>None (use system default)</em>
                    </MenuItem>
                    {(() => {
                      // Group models by provider
                      const grouped = availableModels.reduce<Record<string, LlmModelDto[]>>((acc, model) => {
                        const key = model.providerName;
                        if (!acc[key]) acc[key] = [];
                        acc[key].push(model);
                        return acc;
                      }, {});
                      return Object.entries(grouped).flatMap(([providerName, models]) => [
                        <MenuItem key={`header-${providerName}`} disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary', mt: 1 }}>
                          {providerName}
                        </MenuItem>,
                        ...models.map((model) => (
                          <MenuItem key={model.id} value={model.id}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                              <span>{model.displayName}</span>
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
                                {model.maxContextTokens >= 1000 ? `${Math.round(model.maxContextTokens / 1000)}K` : model.maxContextTokens} ctx
                              </Typography>
                            </Box>
                          </MenuItem>
                        )),
                      ]);
                    })()}
                  </Select>
                </FormControl>
                {availableModels.length === 0 && !modelsLoading && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No models available. Configure LLM providers in Settings to enable model selection.
                  </Typography>
                )}
                {modelsLoading && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Loading available models...
                  </Typography>
                )}
              </Box>

              <Divider />

              {/* Context limits: Last N messages / Last N days */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Context limits
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Restrict how much conversation history is sent to the LLM. Leave empty for no limit.
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <TextField
                    label="Last N messages"
                    type="number"
                    value={contextLimitLastNMessages}
                    onChange={(e) => setContextLimitLastNMessages(e.target.value)}
                    placeholder="No limit"
                    inputProps={{ min: 1, max: 500 }}
                    sx={{ maxWidth: 180 }}
                    helperText="1–500; only the most recent N messages"
                  />
                  <TextField
                    label="Last N days"
                    type="number"
                    value={contextLimitLastNDays}
                    onChange={(e) => setContextLimitLastNDays(e.target.value)}
                    placeholder="No limit"
                    inputProps={{ min: 1, max: 365 }}
                    sx={{ maxWidth: 180 }}
                    helperText="1–365; only messages from the last N days"
                  />
                </Box>
              </Box>

              <Divider />

              {/* System Prompt */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  System Prompt
                </Typography>
                <CodeEditor
                  value={systemPrompt}
                  onChange={setSystemPrompt}
                  language="plaintext"
                  height="300px"
                  placeholder="Enter the system prompt for the agent..."
                />
                {parameters.length > 0 && (
                  <Alert severity="info" icon={false} sx={{ mt: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Use <strong>{'{{key}}'}</strong> Mustache syntax to insert parameter values into the prompt at session creation.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                      {parameters.map((p) => (
                        <Tooltip key={p.key} title="Click to copy">
                          <Chip
                            label={`{{${p.key}}}`}
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              navigator.clipboard.writeText(`{{${p.key}}}`);
                            }}
                            sx={{ cursor: 'pointer', fontFamily: 'monospace' }}
                          />
                        </Tooltip>
                      ))}
                    </Box>
                  </Alert>
                )}
              </Box>

              <Divider />

              {/* Parameters */}
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                  Parameters
                </Typography>
                <ParameterList
                  parameters={parameters}
                  onAdd={handleParameterAdd}
                  onUpdate={handleParameterUpdate}
                  onDelete={handleParameterDelete}
                />
              </Box>

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 6 }}>
                <Button
                  variant="outlined"
                  startIcon={<CancelIcon />}
                  onClick={() => navigate('/agents')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
                </Button>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};
